/**
 * Open Play Live Score Watch Page
 * Public spectator page — shows live score for one court match.
 * Auto-refreshes every 5s. No auth needed.
 * Route: /sports/open-play/watch/:qrToken
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Zap, Activity } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RED = '#E63946';
const BLUE = '#2563EB';

function PlayerCard({ player, color }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div style={{
        width: 72, height: 72, borderRadius: '50%', overflow: 'hidden',
        border: `3px solid ${color}66`, background: '#1A1A1A',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 28, fontWeight: 900, color, fontFamily: 'Oswald, sans-serif',
      }}>
        {player.avatar_url
          ? <img src={player.avatar_url} alt={player.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : player.name?.[0]?.toUpperCase()}
      </div>
      <p className="text-sm font-bold text-center" style={{ color: '#fff', maxWidth: 90 }}>{player.name}</p>
    </div>
  );
}

// Every state the match can be in, worded for a spectator (R1-116).
const STATE = {
  queued: { label: 'UP NEXT', color: '#F59E0B', note: 'Waiting for a court' },
  active: { label: 'LIVE', color: RED, note: 'Live · refreshes automatically' },
  pending: { label: 'AWAITING CONFIRMATION', color: '#F59E0B', note: 'Final score being confirmed' },
  completed: { label: 'MATCH ENDED', color: '#3B82F6', note: null },
  cancelled: { label: 'MATCH CANCELLED', color: '#737373', note: 'This match was not played' },
};
const TERMINAL = new Set(['completed', 'cancelled']);

export default function SportsOpenPlayWatchPage() {
  const { qrToken } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [reconnecting, setReconnecting] = useState(false);
  const [flash, setFlash] = useState(null); // which side just scored
  const prev = useRef(null);                // last score seen — a ref, so the poll never reads a stale one
  const done = useRef(false);

  const load = useCallback(async () => {
    if (done.current) return;
    try {
      const r = await axios.get(`${API}/sports/open-play/watch/${qrToken}`);
      const d = r.data;
      const last = prev.current;
      if (last && (d.score_a !== last.a || d.score_b !== last.b)) {
        setFlash(d.score_a > last.a ? 'a' : d.score_b > last.b ? 'b' : null);
        setTimeout(() => setFlash(null), 800);
      }
      prev.current = { a: d.score_a, b: d.score_b };
      if (TERMINAL.has(d.status)) done.current = true;  // nothing more will change
      setData(d);
      setError('');
      setReconnecting(false);
    } catch (e) {
      // Only a real 404 means "not found"; a network blip keeps the last score.
      if (e.response?.status === 404) setError(e.response?.data?.detail || 'Match not found');
      else setReconnecting(true);
    }
  }, [qrToken]);

  useEffect(() => {
    done.current = false;
    prev.current = null;
    load();
    const iv = setInterval(() => { if (!document.hidden && !done.current) load(); }, 5000);
    return () => clearInterval(iv);
  }, [qrToken, load]);

  if (error && !data) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A' }}>
      <p className="text-lg font-bold" style={{ color: '#EF4444' }}>{error}</p>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A' }}>
      <Zap className="h-8 w-8 animate-pulse" style={{ color: RED }} />
    </div>
  );

  const finished = data.status === 'completed';
  const st = data.status === 'active' && data.pending_confirm ? STATE.pending
    : STATE[data.status] || STATE.active;
  const winner = finished ? data.winner : null;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0A0A0A', color: '#fff' }}>
      {/* Header */}
      <div className="px-4 py-3 text-center" style={{ background: '#000', borderBottom: '1px solid #1A1A1A' }}>
        <div className="flex items-center justify-center gap-2 mb-0.5">
          {data.status === 'active' && <span className="h-2 w-2 rounded-full animate-pulse" style={{ background: st.color }} />}
          <p data-testid="watch-state" className="text-xs font-bold uppercase tracking-widest" style={{ color: st.color }}>
            {st.label}{data.court_name && !TERMINAL.has(data.status) ? ` · ${data.court_name}` : ''}
          </p>
        </div>
        <p className="text-sm font-black uppercase" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>
          {data.session_name}
        </p>
        {data.facility_name && (
          <p className="text-xs mt-0.5" style={{ color: '#555' }}>{data.facility_name}</p>
        )}
      </div>

      {/* Main score card */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 gap-8">
        {/* Teams + Scores */}
        <div className="w-full max-w-sm">
          <div className="grid grid-cols-2 gap-3 mb-6">
            {/* Team A */}
            <div className="flex flex-col items-center gap-3 p-4 rounded-2xl"
              style={{
                background: flash === 'a' ? `${RED}20` : `${RED}10`,
                border: `2px solid ${flash === 'a' ? RED : `${RED}33`}`,
                transition: 'background 0.3s, border-color 0.3s',
              }}>
              {/* Player avatars */}
              <div className="flex gap-2 justify-center flex-wrap">
                {data.team_a_players.map(p => <PlayerCard key={p.id} player={p} color={RED} />)}
              </div>
              {/* Score */}
              <p className="font-black tabular-nums leading-none" style={{ color: RED, fontSize: 72, fontFamily: 'Oswald, sans-serif' }}>
                {data.score_a}
              </p>
              {winner === 'a' && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: `${RED}20`, color: RED }}>WINNER</span>
              )}
            </div>

            {/* Team B */}
            <div className="flex flex-col items-center gap-3 p-4 rounded-2xl"
              style={{
                background: flash === 'b' ? `${BLUE}20` : `${BLUE}10`,
                border: `2px solid ${flash === 'b' ? BLUE : `${BLUE}33`}`,
                transition: 'background 0.3s, border-color 0.3s',
              }}>
              <div className="flex gap-2 justify-center flex-wrap">
                {data.team_b_players.map(p => <PlayerCard key={p.id} player={p} color={BLUE} />)}
              </div>
              <p className="font-black tabular-nums leading-none" style={{ color: BLUE, fontSize: 72, fontFamily: 'Oswald, sans-serif' }}>
                {data.score_b}
              </p>
              {winner === 'b' && (
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: `${BLUE}20`, color: BLUE }}>WINNER</span>
              )}
            </div>
          </div>

          {/* VS */}
          <div className="text-center">
            <span className="text-2xl font-black" style={{ color: '#333', fontFamily: 'Oswald, sans-serif' }}>VS</span>
          </div>
        </div>

        {/* Status */}
        {(st.note || reconnecting) && (
          <div className="flex items-center gap-2">
            {data.status === 'active' && <Activity className="h-4 w-4 animate-pulse" style={{ color: st.color }} />}
            <span data-testid="watch-note" className="text-xs font-bold" style={{ color: reconnecting ? '#F59E0B' : '#555' }}>
              {reconnecting ? 'Reconnecting…' : st.note}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
